import React, { useState } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useSelector } from "react-redux";

export const toMediaUrl = (img) => {
  if (!img || typeof img !== "string") return "";
  if (img.startsWith("http")) return img;
  return `${process.env.REACT_APP_CDN_URL}/${process.env.REACT_APP_PACKAGE_NAME}/${img.replace(/^\//, "")}`;
};

export const collectProductMedia = (urls, videos) => {
  const images = (Array.isArray(urls) ? urls : [])
    .map((src) => ({ type: "image", src: toMediaUrl(src) }))
    .filter((item) => item.src);
  const clips = (Array.isArray(videos) ? videos : [])
    .map((src) => ({ type: "video", src: toMediaUrl(src) }))
    .filter((item) => item.src);
  return [...images, ...clips];
};

export default function ProductMediaThumb({
  urls = [],
  videos = [],
  name = "",
  width = 160,
  height = 160,
  borderRadius = "10px",
}) {
  const { activeAppTheme } = useSelector((state) => state.dashboard || {});
  const theme = activeAppTheme || {};
  const themeColors = {
    scaffoldGradientColors: theme.scaffoldGradientColors?.length
      ? `linear-gradient(135deg, ${theme.scaffoldGradientColors[0]}, ${theme.scaffoldGradientColors[1]})`
      : "#ffffff",
    mediumContainer: theme.mediumContainerGradientColors?.length
      ? `linear-gradient(135deg, ${theme.mediumContainerGradientColors[0]}, ${theme.mediumContainerGradientColors[1]})`
      : "#ffffff",
    bigContainer: theme.bigContainerGradientColors?.length
      ? `linear-gradient(135deg, ${theme.bigContainerGradientColors[0]}, ${theme.bigContainerGradientColors[1]})`
      : "#ffffff",
    smallContainer: theme.smallContainerGradientColors?.length
      ? `linear-gradient(135deg, ${theme.smallContainerGradientColors[0]}, ${theme.smallContainerGradientColors[1]})`
      : "#ffffff",
    scaffold: theme.scaffoldColor || "#ffffff",
    border: theme.containerBorder || "#e0e0e0",
    text: theme.textColor || "#000000",
    appBar: theme.appBarColor || "#000000",
    primary: theme.primaryColor || "#FFD700",
  };

  const media = collectProductMedia(urls, videos);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const extra = Math.max(media.length - 1, 0);
  const current = media[index];

  const openPreview = (event) => {
    event.stopPropagation();
    if (!media.length) return;
    setIndex(0);
    setOpen(true);
  };

  const step = (delta) => {
    setIndex((prev) => (prev + delta + media.length) % media.length);
  };

  return (
    <>
      <Box
        onClick={openPreview}
        sx={{
          position: "relative",
          width,
          height,
          borderRadius,
          overflow: "hidden",
          cursor: media.length ? "pointer" : "default",
          mx: "auto",
          bgcolor: themeColors.smallContainer,
          border: `1px solid ${themeColors.border}`,
        }}
      >
        {media[0]?.type === "video" ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              background: themeColors.appBar,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PlayArrowIcon sx={{ color: themeColors.primary, fontSize: 40 }} />
          </Box>
        ) : (
          <Box
            component="img"
            src={media[0]?.src || "/images/logo.png"}
            alt={name || "Product"}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        )}
        {extra > 0 && (
          <Box
            sx={{
              position: "absolute",
              right: 8,
              bottom: 8,
              bgcolor: themeColors.primary,
              color: themeColors.text,
              border: `1px solid ${themeColors.border}`,
              borderRadius: "8px",
              px: 0.9,
              py: 0.3,
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1.4,
            }}
          >
            +{extra}
          </Box>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              overflow: "hidden",
              background: themeColors.bigContainer,
              color: themeColors.text,
              border: `1px solid ${themeColors.border}`,
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            px: 2.5,
            py: 1.5,
            background: themeColors.primary,
            color: themeColors.text,
            borderBottom: `1px solid ${themeColors.border}`,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: themeColors.text }}>
              Product Media Preview
            </Typography>
            <Typography sx={{ fontSize: 14, color: themeColors.text }}>{name}</Typography>
          </Box>
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ color: themeColors.text }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            px: { xs: 1.5, sm: 3 },
            pt: 2,
            pb: 2.5,
            background: themeColors.scaffoldGradientColors,
            color: themeColors.text,
          }}
        >
          <Box
            sx={{
              position: "relative",
              background: themeColors.mediumContainer,
              borderRadius: 1,
              minHeight: { xs: 220, sm: 320 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {media.length > 1 && (
              <IconButton
                onClick={() => step(-1)}
                aria-label="Previous"
                sx={{
                  position: "absolute",
                  left: 8,
                  zIndex: 1,
                  background: themeColors.primary,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.border}`,
                  boxShadow: 1,
                  "&:hover": { background: themeColors.smallContainer },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}

            {current?.type === "video" ? (
              <Box
                component="video"
                key={current.src}
                src={current.src}
                controls
                playsInline
                sx={{
                  width: "100%",
                  maxHeight: { xs: 240, sm: 360 },
                  bgcolor: themeColors.appBar,
                  display: "block",
                }}
              />
            ) : (
              <Box
                component="img"
                src={current?.src || "/images/logo.png"}
                alt={name || "Product"}
                sx={{
                  width: "100%",
                  maxHeight: { xs: 240, sm: 360 },
                  objectFit: "contain",
                  display: "block",
                }}
              />
            )}

            {media.length > 1 && (
              <IconButton
                onClick={() => step(1)}
                aria-label="Next"
                sx={{
                  position: "absolute",
                  right: 8,
                  zIndex: 1,
                  background: themeColors.primary,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.border}`,
                  boxShadow: 1,
                  "&:hover": { background: themeColors.smallContainer },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            )}
          </Box>

          {media.length > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 1.2,
                mt: 2,
                overflowX: "auto",
              }}
            >
              {media.map((item, itemIndex) => {
                const selected = itemIndex === index;
                return (
                  <Box
                    key={`${item.src}-${itemIndex}`}
                    onClick={() => setIndex(itemIndex)}
                    sx={{
                      width: 64,
                      height: 64,
                      flex: "0 0 auto",
                      borderRadius: 1.5,
                      overflow: "hidden",
                      cursor: "pointer",
                      border: selected
                        ? `2px solid ${themeColors.primary}`
                        : `2px solid ${themeColors.border}`,
                      position: "relative",
                      background: themeColors.smallContainer,
                    }}
                  >
                    {item.type === "video" ? (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          background: themeColors.appBar,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PlayArrowIcon sx={{ color: themeColors.primary }} />
                      </Box>
                    ) : (
                      <Box
                        component="img"
                        src={item.src}
                        alt=""
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          <Typography
            sx={{
              textAlign: "center",
              mt: 1.5,
              color: themeColors.text,
              fontSize: 14,
            }}
          >
            {media.length ? `${index + 1} of ${media.length}` : ""}
            {current ? `   ${current.type === "video" ? "Video" : "Image"}` : ""}
          </Typography>
        </Box>
      </Dialog>
    </>
  );
}
